import { usePlatformLocale } from "../lib/platform-language";
import { useEffect, useRef } from "react";
import type { Locale } from "../lib/i18n";

type TurnstileTheme = "light" | "dark";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme: TurnstileTheme;
      language: "auto" | "en";
      size: "flexible";
      appearance: "always";
      action: string;
      retry: "auto";
      "refresh-expired": "auto";
      "response-field": false;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile API did not initialize."));
    };
    script.onerror = () => reject(new Error("Turnstile API could not be loaded."));
    document.head.append(script);
  });

  return turnstileScriptPromise;
}

export function TurnstileWidget({
  siteKey,
  theme,
  locale,
  resetNonce,
  onToken,
  onError,
}: {
  siteKey: string;
  theme: TurnstileTheme;
  locale: Locale;
  resetNonce: number;
  onToken: (token: string | null) => void;
  onError: () => void;
}) {
  usePlatformLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
  }, [onError, onToken]);

  useEffect(() => {
    let cancelled = false;
    onTokenRef.current(null);

    void loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          // Turnstile does not support Albanian. "auto" avoids sending the
          // unsupported `sq` code while English remains explicit.
          language: locale === "en" ? "en" : "auto",
          size: "flexible",
          appearance: "always",
          action: "local-login",
          retry: "auto",
          "refresh-expired": "auto",
          "response-field": false,
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => {
            onTokenRef.current(null);
            onErrorRef.current();
          },
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [locale, siteKey, theme]);

  useEffect(() => {
    if (resetNonce > 0 && widgetIdRef.current && window.turnstile) {
      onTokenRef.current(null);
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetNonce]);

  return <div className="turnstile-widget" ref={containerRef} aria-label="Cloudflare Turnstile" />;
}
